import { z } from 'zod';
import { CHAMPION_ID_PATTERN } from './champion.js';

const match = z.enum(['correct', 'partial', 'incorrect']);

export const feedbackSchema = z.object({
  gender: match,
  positions: match,
  species: match,
  resource: match,
  rangeType: match,
  regions: match,
  releaseYear: z.object({
    result: z.enum(['correct', 'incorrect']),
    arrow: z.enum(['up', 'down']).nullable(),
  }),
});
export type FeedbackView = z.infer<typeof feedbackSchema>;

export const guessRequestSchema = z.object({
  championId: z.string().regex(CHAMPION_ID_PATTERN),
});
export type GuessRequest = z.infer<typeof guessRequestSchema>;

/** The guessed champion's own values, shown in the board cells. Never the target's. */
export const attributesSchema = z.object({
  gender: z.string(),
  positions: z.array(z.string()),
  species: z.array(z.string()),
  resource: z.string(),
  rangeType: z.array(z.string()),
  regions: z.array(z.string()),
  releaseYear: z.number().int(),
});
export type AttributesView = z.infer<typeof attributesSchema>;

export const guessViewSchema = z.object({
  seq: z.number().int().min(1),
  championId: z.string(),
  attributes: attributesSchema,
  feedback: feedbackSchema,
  msSinceStart: z.number().int().min(0),
});
export type GuessView = z.infer<typeof guessViewSchema>;

/**
 * What a client may know about a game. While it is in progress there is deliberately no
 * field that could carry the hidden champion; `answer` appears only once the game has ended.
 */
export const gameViewSchema = z.object({
  id: z.string(),
  status: z.enum(['in_progress', 'won', 'abandoned']),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  elapsedMs: z.number().int().min(0),
  guesses: z.array(guessViewSchema),
  answer: z.object({ championId: z.string() }).nullable(),
});
export type GameView = z.infer<typeof gameViewSchema>;

export const gameResponseSchema = z.object({ game: gameViewSchema });
export type GameResponse = z.infer<typeof gameResponseSchema>;

export const currentGameResponseSchema = z.object({ game: gameViewSchema.nullable() });
export type CurrentGameResponse = z.infer<typeof currentGameResponseSchema>;

/** The type-ahead list: names and images only, never attributes (D-012). */
export const championListSchema = z.object({
  datasetVersion: z.number().int(),
  champions: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      aliases: z.array(z.string()),
      avatar: z.string(),
    }),
  ),
});
export type ChampionList = z.infer<typeof championListSchema>;
